import fetchEndpoint from '@/services/fetchEndpoint';

export type TimelineItemDto = {
  pmYear: number;
  citationCount: number;
};

export type EvidenceTimelineFetchResult = {
  timeline: TimelineItemDto[] | null;
  publicationsByYear?: Record<number, unknown> | null;
};

function normalizeTimelineItem(it: unknown): TimelineItemDto | null {
  if (!it || typeof it !== 'object') return null;
  const o = it as Record<string, unknown>;
  const pmYear = Number(o.pmYear ?? o.year);
  const citationCount = Number(o.citationCount ?? o.evidence ?? o.citations);
  if (!Number.isFinite(pmYear) || !Number.isFinite(citationCount)) return null;
  return { pmYear, citationCount };
}

function normalizeTimelineArray(raw: unknown): TimelineItemDto[] {
  if (!Array.isArray(raw)) return [];
  const out: TimelineItemDto[] = [];
  for (const it of raw) {
    const n = normalizeTimelineItem(it);
    if (n) out.push(n);
  }
  return out;
}

export function extractTimelineFromEvidencesPayload(payload: unknown): EvidenceTimelineFetchResult {
  if (payload == null) return { timeline: null };
  if (Array.isArray(payload)) {
    const t = normalizeTimelineArray(payload);
    return { timeline: t.length ? t : null };
  }
  if (typeof payload !== 'object') return { timeline: null };
  const p = payload as Record<string, unknown>;

  if (Array.isArray(p.timeline)) {
    const timeline = normalizeTimelineArray(p.timeline);
    return {
      timeline: timeline.length ? timeline : null,
      publicationsByYear: (p.publicationsByYear as Record<number, unknown>) ?? null,
    };
  }

  if (p.payload && typeof p.payload === 'object') {
    return extractTimelineFromEvidencesPayload(p.payload);
  }

  if (Array.isArray(p.data)) {
    const timeline = normalizeTimelineArray(p.data);
    return { timeline: timeline.length ? timeline : null };
  }

  return { timeline: null };
}

export function buildGdaEvidencesWithTimelineQuery(params: {
  source: string;
  diseaseFilter: string;
  page_number?: number;
  page_size?: number;
  tab?: string;
  geneSymbol?: string;
  variantName?: string;
}): string {
  const tab = (params.tab ?? 'gda').toLowerCase();
  const diseaseFilter = String(params.diseaseFilter ?? '').replace(/^UMLS_/, '');
  const search = new URLSearchParams({
    source: params.source,
    page_number: String(params.page_number ?? 0),
    page_size: String(params.page_size ?? 50),
    disease_filter: diseaseFilter,
  });
  if (tab === 'vda') {
    if (params.variantName) search.set('variant_name', params.variantName);
  } else if (params.geneSymbol) {
    search.set('gene_symbol', params.geneSymbol);
  }
  return `api/${tab}/evidences-with-timeline?${search.toString()}`;
}

export async function fetchGdaEvidencesWithTimeline(params: {
  source: string;
  diseaseFilter: string;
  page_number?: number;
  page_size?: number;
  authorization: unknown;
  setAuthError: (e: unknown) => void;
  tab?: string;
  geneSymbol?: string;
  variantName?: string;
}): Promise<EvidenceTimelineFetchResult> {
  const endpoint = buildGdaEvidencesWithTimelineQuery(params);
  const res = (await fetchEndpoint(endpoint, params.authorization, params.setAuthError)) as {
    status?: string;
    payload?: unknown;
  };
  if (res?.status !== 'OK') {
    return { timeline: null };
  }
  return extractTimelineFromEvidencesPayload(res.payload);
}
